/**
 * Modale "Relancer un client".
 *
 * Choix du canal (message / appel / email), message suggéré modifiable, action principale verte.
 * Le bouton principal s'adapte au canal choisi.
 */
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Textarea } from '../../ui/textarea';
import {
  X, MessageCircle, Phone, Mail, Send, Info, CheckCircle2, ChevronRight,
} from 'lucide-react';
import { useClientActions } from './ClientActionsContext';
import { useToast } from '../../../hooks/use-toast';
import {
  initials, initialsBg, buildSuggestedMessage, daysSince, fmtDateFr,
} from './shared';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNELS = [
  {
    id: 'message',
    label: 'Envoyer un message',
    description: 'Envoyez un message personnalisé via la messagerie intégrée.',
    icon: MessageCircle,
    cta: 'Envoyer le message',
  },
  {
    id: 'call',
    label: 'Appeler le client',
    description: 'Appelez directement le client sur son numéro de téléphone.',
    icon: Phone,
    cta: "Lancer l'appel",
  },
  {
    id: 'email',
    label: 'Envoyer un email',
    description: 'Envoyez un email de relance avec une offre adaptée.',
    icon: Mail,
    cta: "Envoyer l'email",
  },
];

export default function RelancerClientModal() {
  const { modal, client, close, switchModal } = useClientActions();
  const { toast } = useToast();
  const [channel, setChannel] = useState('message');
  const [sending, setSending] = useState(false);
  const isOpen = modal === 'relance' && !!client;

  const inactiveDays = useMemo(() => {
    if (client?.daysSinceLast !== undefined && client?.daysSinceLast !== null) return client.daysSinceLast;
    return daysSince(client?.lastSeen);
  }, [client]);

  const [message, setMessage] = useState(() => buildSuggestedMessage(client?.companyName, inactiveDays));

  // Recalculer le message à chaque changement de client (modal réutilisée)
  React.useEffect(() => {
    if (client) setMessage(buildSuggestedMessage(client.companyName, inactiveDays));
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [client?.companyName, inactiveDays]);

  if (!isOpen) return null;

  const currentChannel = CHANNELS.find((c) => c.id === channel) || CHANNELS[0];

  const handleSubmit = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/admin/customer-success/actions`, {
        companyName: client.companyName,
        clientEmail: client.primaryEmail,
        action: 'message',
        channel: 'internal_messaging',
        body: message,
        visibility: 'commercial',
        metadata: { trigger: 'relance' },
      });
      toast({ title: 'Message envoyé avec succès', description: `${client.companyName} sera contacté(e).` });
      close();
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  // Si le canal est message → on garde la zone de saisie inline.
  // Si appel/email → propose de basculer vers la modale spécifique.
  const switchToSpecific = () => {
    if (channel === 'call') switchModal('call');
    else if (channel === 'email') switchModal('email');
    else handleSubmit();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && close()}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0"
        data-testid="relance-modal"
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
              <Phone className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Relancer un client</h2>
              <p className="text-xs text-gray-500">Contactez ce client pour raviver la relation.</p>
            </div>
          </div>
          <button onClick={close} className="text-gray-400 hover:text-gray-600" data-testid="close-relance-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-auto flex-1 px-6 pb-4 space-y-4">
          {/* Carte client */}
          <div className="border border-gray-100 rounded-lg p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`w-11 h-11 rounded-full ${initialsBg(client.companyName)} text-white flex items-center justify-center text-sm font-semibold shrink-0`}>
                {initials(client.companyName)}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{client.companyName}</p>
                <p className="text-[11px] text-gray-500 truncate">
                  {client.primaryEmail || '—'}{client.primaryPhone ? ` • ${client.primaryPhone}` : ''}
                </p>
              </div>
            </div>
            <div className="text-right">
              {inactiveDays !== null && (
                <Badge className="bg-amber-50 text-amber-700 border-amber-200 border text-[10px]">
                  Inactif depuis {inactiveDays} jour{inactiveDays > 1 ? 's' : ''}
                </Badge>
              )}
              {client.lastSeen && (
                <p className="text-[10px] text-gray-400 mt-1">
                  Dernière activité : {fmtDateFr(client.lastSeen)}
                </p>
              )}
            </div>
          </div>

          {/* Info banner */}
          <div className="bg-blue-50/60 border border-blue-100 rounded-lg p-3 flex items-start gap-2 text-xs text-blue-900">
            <Info className="w-4 h-4 shrink-0 mt-0.5 text-blue-500" />
            <p>
              {inactiveDays !== null
                ? `Ce client n'a pas effectué de demande depuis ${inactiveDays} jour${inactiveDays > 1 ? 's' : ''}. Une relance peut permettre de réactiver la relation.`
                : "Une relance peut permettre de réactiver la relation avec ce client."}
            </p>
          </div>

          {/* Canaux */}
          <div>
            <p className="text-xs font-semibold text-gray-700 mb-2">Choisir un canal de contact</p>
            <div className="space-y-2">
              {CHANNELS.map((c) => {
                const Icon = c.icon;
                const isActive = channel === c.id;
                return (
                  <button
                    key={c.id}
                    onClick={() => setChannel(c.id)}
                    className={`w-full text-left rounded-lg p-3 border transition-all flex items-center justify-between gap-3 ${
                      isActive
                        ? 'bg-emerald-50 border-emerald-300 ring-1 ring-emerald-200'
                        : 'bg-white border-gray-100 hover:border-gray-200'
                    }`}
                    data-testid={`channel-${c.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                        isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-50 text-gray-500'
                      }`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">{c.label}</p>
                        <p className="text-[11px] text-gray-500 truncate">{c.description}</p>
                      </div>
                    </div>
                    {isActive ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Message suggéré (uniquement si canal = message) */}
          {channel === 'message' && (
            <div data-testid="relance-message-block">
              <p className="text-xs font-semibold text-gray-700 mb-2">Message suggéré</p>
              <Textarea
                rows={9}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="bg-emerald-50/30 border-emerald-100 font-mono text-xs"
                data-testid="relance-message-textarea"
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-2">
          <Button variant="ghost" size="sm" onClick={close} data-testid="relance-cancel-btn">
            Annuler
          </Button>
          <Button
            size="sm"
            onClick={switchToSpecific}
            disabled={sending || (channel === 'message' && !message.trim())}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="relance-submit-btn"
          >
            <Send className="w-3.5 h-3.5 mr-1.5" />
            {sending ? 'Envoi...' : currentChannel.cta}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
